import { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { poActions } from "src/context/po-slice";
import { RootState } from "src/context/store";
import FilterAccordion from "./FilterAccordion";
import { Button } from "src/components";

interface FilterItem {
  id?: string;
  name?: string;
  code?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // Add an index signature
}

interface POFilter {
  onCloseClick: () => void;
  companyDetails: FilterItem[];
  divisionDetails: FilterItem[];
  stateDetails?: FilterItem[];
  statusDetails?: FilterItem[];
  blockTypeDetails?: FilterItem[];
  cndnRemarkDetails?: FilterItem[];
  source: string
}

interface CommonFilters {
  company?: any;
  division?: any;
  state?: any;
  status?: any;
  blockType?: any;
  cndnRemark?: any;
}

const Divider = () => <div className="w-full h-[0.0625rem] my-2 bg-[#0000001a]" />;

const POFilter = forwardRef<HTMLElement, POFilter>(
  (
    {
      companyDetails,
      divisionDetails,
      stateDetails,
      statusDetails,
      blockTypeDetails,
      cndnRemarkDetails,
      source
    },
    ref
  ) => {
    const { stockistBlockFilters, stockistRevokeFilters, orderTypeExceptionFilters } = useSelector((state: RootState) => state.po);
    const dispatch = useDispatch();

    const companyFilterHandler = (details: FilterItem[]) => {
      if (source === "StockistBlock") dispatch(poActions.setStockistBlockFilters({ key: "company", value: details }));
      else if (source === "StockistRevoke") dispatch(poActions.setStockistRevokeFilters({ key: "company", value: details }));
      else {
        dispatch(poActions.setOrderTypeExceptionFilters({ key: "company", value: details }));
      }
    };

    const divisionFilterHandler = (details: FilterItem[]) => {
      if (source === "StockistBlock") dispatch(poActions.setStockistBlockFilters({ key: "division", value: details }));
      else if (source === "StockistRevoke") dispatch(poActions.setStockistRevokeFilters({ key: "division", value: details }));
      else {
        dispatch(poActions.setOrderTypeExceptionFilters({ key: "division", value: details }));
      }
    };

    const stateFilterHandler = (details: FilterItem[]) => {
      dispatch(poActions.setOrderTypeExceptionFilters({ key: "state", value: details }));
    };

    const statusFilterHandler = (details: FilterItem[]) => {
      dispatch(poActions.setStockistBlockFilters({ key: "status", value: details }));

    };

    const blockTypeFilterHandler = (details: FilterItem[]) => {
      dispatch(poActions.setStockistBlockFilters({ key: "blockType", value: details }));

    };

    const cndnRemarksFilterHandler = (details: FilterItem[]) => {
      dispatch(poActions.setOrderTypeExceptionFilters({ key: "cndnRemark", value: details }));
    };

    const resetFilter = () => {
      if (source === "StockistBlock") {
        dispatch(poActions.setStockistBlockFilters({ key: "company", value: [] }));
        dispatch(poActions.setStockistBlockFilters({ key: "division", value: [] }));
      } else if (source === "StockistRevoke") {
        dispatch(poActions.setStockistRevokeFilters({ key: "company", value: [] }));
        dispatch(poActions.setStockistRevokeFilters({ key: "division", value: [] }));
      }
      else {
        dispatch(poActions.setOrderTypeExceptionFilters({ key: "company", value: [] }));
        dispatch(poActions.setOrderTypeExceptionFilters({ key: "division", value: [] }));
        dispatch(poActions.setOrderTypeExceptionFilters({ key: "state", value: [] }));
        dispatch(poActions.setOrderTypeExceptionFilters({ key: "cndnRemark", value: [] }));
      }
      dispatch(poActions.setIsResetFilter(false));
    };

    const filters: CommonFilters = source === "StockistBlock" ? stockistBlockFilters : source === "StockistRevoke" ? stockistRevokeFilters : orderTypeExceptionFilters;

    return (
      <aside
        className="bg-white border-l-[1px] min-w-[20rem] overflow-y-auto max-w-[25rem] border-l-[#00000014] fixed px-8 text-[#49484C] text-xl font-semibold py-5 right-0  h-screen"
        ref={ref}
      >
        <div className="flex items-center justify-between gap-5 mt-3">
          <h3 className="whitespace-nowrap">Fetch filters</h3>
          <Button className="border py-1 bg-[#7F56D9] text-white" onClick={resetFilter}>
            Reset
          </Button>
        </div>

        {/* filters  */}
        <div className="flex flex-col ">
          <FilterAccordion
            uniqueKey="code"
            displayKey="shortname"
            onSelectingFilter={companyFilterHandler}
            title="Company Name"
            arrayToFilter={companyDetails}
            defaultValue={filters.company}
          />

          <>
            <Divider />
            <FilterAccordion
              uniqueKey="division_name"
              displayKey="division_name"
              title="Division"
              arrayToFilter={divisionDetails}
              onSelectingFilter={divisionFilterHandler}
              defaultValue={filters.division}
            />
          </>

          {stateDetails &&
            <>
              <Divider />
              <FilterAccordion
                uniqueKey="state_code"
                displayKey="state_name"
                title="State"
                defaultValue={filters.state}
                onSelectingFilter={stateFilterHandler}
                arrayToFilter={stateDetails || []}
              />
            </>}

          {statusDetails &&
            <>
              <Divider />
              <FilterAccordion
                uniqueKey="value"
                displayKey="name"
                title="Status"
                defaultValue={filters.status}
                onSelectingFilter={statusFilterHandler}
                arrayToFilter={statusDetails || []}
              />
            </>}

          {blockTypeDetails &&
            <>
              <Divider />
              <FilterAccordion
                uniqueKey="value"
                displayKey="name"
                title="Block Type"
                defaultValue={filters.blockType}
                onSelectingFilter={blockTypeFilterHandler}
                arrayToFilter={blockTypeDetails || []}
              />
            </>}

          {cndnRemarkDetails &&
            <>
              <Divider />
              <FilterAccordion
                uniqueKey="cndn_remarks_code_int"
                displayKey="cndn_remarks_code_name"
                title="CNDN Remarks"
                defaultValue={filters.cndnRemark}
                onSelectingFilter={cndnRemarksFilterHandler}
                arrayToFilter={cndnRemarkDetails || []}
              />
            </>
          }
        </div>
      </aside>
    );
  }
);

export default POFilter;
